(() => {
  'use strict';
  const mounted = new WeakMap();
  const selector = '[data-clamp-pdp]';
  const initialize = (root) => {
    if (mounted.has(root)) return;
    const controller = new AbortController();
    const listen = (node, type, handler, options = {}) => node?.addEventListener(type, handler, {...options, signal: controller.signal});
    const one = (query) => root.querySelector(query);
    const variantInput = one('[data-jj-variant-select]');
    const quantityInput = one('.jj-product-form [name="quantity"]');
    const quantityButtons = Array.from(root.querySelectorAll('[data-cp-quantity-step]'));
    const stage = one('[data-jj-gallery-current]');
    const dialog = one('[data-cp-dialog]');
    const zoomContent = one('[data-cp-zoom-content]');
    let data = {};
    try { data = JSON.parse(one('[data-jj-product-data]')?.textContent || '{}'); } catch (_) {}
    const variants = Array.isArray(data.variants) ? data.variants : [];
    const selected = () => variants.find((variant) => String(variant.id) === String(variantInput?.value));
    let disposed = false;
    let zoomOpener = null;
    let previousOverflow = null;
    const positiveInteger = (value, fallback) => {
      const parsed = Number(value);
      return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : fallback;
    };
    const limits = () => {
      const variant = selected();
      const min = positiveInteger(variant?.quantityMin, 1);
      const step = positiveInteger(variant?.quantityIncrement, 1);
      const rawMax = positiveInteger(variant?.quantityMax, Infinity);
      const max = Number.isFinite(rawMax) ? min + Math.floor((rawMax - min) / step) * step : Infinity;
      return {min, step, max};
    };
    const syncQuantity = (value = quantityInput?.value) => {
      if (!quantityInput) return;
      const {min, step, max} = limits();
      quantityInput.min = String(min);
      quantityInput.step = String(step);
      if (Number.isFinite(max)) quantityInput.max = String(max);
      else quantityInput.removeAttribute('max');
      let quantity = Number(value);
      if (!Number.isSafeInteger(quantity)) quantity = min;
      quantity = Math.max(min, Math.min(max, quantity));
      quantity = min + Math.floor((quantity - min) / step) * step;
      quantityInput.value = String(quantity);
      quantityButtons.forEach((button) => {
        const direction = Number(button.dataset.cpQuantityStep);
        button.disabled = selected()?.available === false || max < min || (direction < 0 ? quantity <= min : quantity >= max);
        button.setAttribute('aria-disabled', String(button.disabled));
      });
      return quantity;
    };
    const copyZoomMedia = () => {
      if (!stage || !zoomContent) return;
      zoomContent.replaceChildren(...Array.from(stage.childNodes, (node) => node.cloneNode(true)));
      zoomContent.querySelectorAll('img').forEach((image) => { image.loading = 'eager'; image.sizes = image.hasAttribute('data-jj-cropped') ? '(max-width: 900px) 213.333vw, 2347px' : '(max-width: 900px) 100vw, 1100px'; });
    };
    const restoreZoom = () => {
      if (previousOverflow !== null) {
        document.body.style.overflow = previousOverflow;
        previousOverflow = null;
      }
      if (!disposed && zoomOpener?.isConnected) zoomOpener.focus({preventScroll: true});
      zoomOpener = null;
    };
    const closeZoom = () => { if (dialog?.open) dialog.close(); restoreZoom(); };
    const openZoom = (button) => {
      if (!dialog || !zoomContent || !stage?.childNodes.length || typeof dialog.showModal !== 'function') return;
      copyZoomMedia();
      if (dialog.open) return;
      zoomOpener = button;
      try { dialog.showModal(); } catch (_) { zoomOpener = null; return; }
      previousOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      dialog.querySelector('[data-cp-close]')?.focus({preventScroll: true});
    };
    listen(root, 'click', (event) => {
      const target = event.target?.closest?.('[data-cp-quantity-step], [data-cp-zoom], [data-cp-close]');
      if (!target || target.closest(selector) !== root) return;
      event.preventDefault();
      if (target.hasAttribute('data-cp-quantity-step')) {
        if (!quantityInput || target.disabled) return;
        const direction = Number(target.dataset.cpQuantityStep);
        if (direction !== -1 && direction !== 1) return;
        const quantity = syncQuantity();
        syncQuantity(quantity + direction * limits().step);
        quantityInput.dispatchEvent(new Event('change', {bubbles: true}));
      } else if (target.hasAttribute('data-cp-close')) closeZoom();
      else openZoom(target);
    });
    listen(variantInput, 'change', () => syncQuantity());
    listen(quantityInput, 'change', () => syncQuantity());
    listen(root, 'sopami:variantchange', () => { syncQuantity(); if (dialog?.open) copyZoomMedia(); });
    listen(dialog, 'cancel', (event) => { event.preventDefault(); closeZoom(); });
    listen(dialog, 'close', restoreZoom);
    listen(dialog, 'click', (event) => {
      if (event.target !== dialog) return;
      const bounds = dialog.getBoundingClientRect();
      if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) closeZoom();
    });
    mounted.set(root, () => {
      disposed = true;
      closeZoom();
      root.querySelectorAll('video').forEach((video) => video.pause());
      controller.abort();
      mounted.delete(root);
    });
    syncQuantity();
  };
  const scan = (scope = document) => {
    if (scope.matches?.(selector)) initialize(scope);
    scope.querySelectorAll?.(selector).forEach(initialize);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => scan(), {once: true}); else scan();
  document.addEventListener('shopify:section:load', (event) => scan(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    if (event.target.matches?.(selector)) mounted.get(event.target)?.();
    event.target.querySelectorAll?.(selector).forEach((root) => mounted.get(root)?.());
  });
})();
